import React, { useState } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from 'src/shadcn/components/ui';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { cn } from 'src/helpers';


interface MonthYearPickerProps {
    value?: { month: number; year: number };
    onChange?: (value: { month: number; year: number }) => void;
    placeholder?: string;
    className?: string;
}

const MONTHS = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

const MONTH_NAMES = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

export function MonthYearPicker({
    value,
    onChange,
    placeholder = "Select month and year",
    className
}: MonthYearPickerProps) {

    console.log({value });
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const [isOpen, setIsOpen] = useState(false);
    const [displayYear, setDisplayYear] = useState(value?.year || currentYear);
    const [showYearGrid, setShowYearGrid] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || null);

    // Generate years around the display year for grid view
    const generateYears = () => {
        const startYear = Math.floor(displayYear / 12) * 12;
        return Array.from({ length: 12 }, (_, i) => startYear + i);
    };

    const handleYearChange = (newYear: number) => {
        setDisplayYear(newYear);
        setShowYearGrid(false);
    };

    const handleMonthSelect = (monthIndex: number) => {
        const newValue = { month: monthIndex, year: displayYear };
        setSelectedValue(newValue);
        onChange?.(newValue);
        setIsOpen(false);
    };

    const handleYearGridNavigation = (direction: 'prev' | 'next') => {
        const change = direction === 'prev' ? -12 : 12;
        setDisplayYear(prev => prev + change);
    };

    const formatDisplayValue = () => {
        if (!selectedValue) return placeholder;
        return `${MONTH_NAMES[selectedValue.month]} ${selectedValue.year}`;
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="secondary"
                    className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedValue && "text-muted-foreground",
                        className
                    )}
                >
                    <Calendar className="mr-2 h-4 w-4" />
                    {formatDisplayValue()}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="start">
                <div className="py-2 px-4">
                    {/* Year Navigation */}
                    <div className="flex items-center justify-between mb-2">
                        {showYearGrid ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleYearGridNavigation('prev')}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span className="font-semibold text-sm">
                                    {generateYears()[0]} - {generateYears()[11]}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleYearGridNavigation('next')}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => setDisplayYear(prev => prev - 1)}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="font-semibold"
                                    onClick={() => setShowYearGrid(true)}
                                >
                                    {displayYear}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => setDisplayYear(prev => prev + 1)}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Year Grid View */}
                    {showYearGrid && (
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {generateYears().map((year) => (
                                <Button
                                    key={year}
                                    variant="outlinePrimary"
                                    size="sm"
                                    className={cn(
                                        "h-7 text-xs text-gray-500 border-gray-200 ",
                                        year === displayYear && "bg-primary text-primary-foreground",
                                        year === currentYear && "border-primary"
                                    )}
                                    onClick={() => handleYearChange(year)}
                                >
                                    {year}
                                </Button>
                            ))}
                        </div>
                    )}

                    {/* Month Grid */}
                    {!showYearGrid && (
                        <div className="grid grid-cols-3 gap-2">
                            {MONTHS.map((month, index) => (
                                <Button
                                    key={month}
                                    variant="outlinePrimary"
                                    size="sm"
                                    className={cn(
                                        "h-7 text-xs text-gray-500 border-gray-200 ",
                                        selectedValue?.month === index &&
                                        selectedValue?.year === displayYear &&
                                        "bg-primary text-primary-foreground",
                                        index === currentMonth &&
                                        displayYear === currentYear &&
                                        "border-primary"
                                    )}
                                    onClick={() => handleMonthSelect(index)}
                                >
                                    {month}
                                </Button>
                            ))}
                        </div>
                    )}

                    {/* Footer with current date indicator */}
                    <div className="mt-2 pt-3 border-t">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs text-muted-foreground"
                            onClick={() => {
                                const currentValue = { month: currentMonth, year: currentYear };
                                setSelectedValue(currentValue);
                                setDisplayYear(currentYear);
                                onChange?.(currentValue);
                                setIsOpen(false);
                            }}
                        >
                            Today: {MONTH_NAMES[currentMonth]} {currentYear}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}